import React, { useEffect, useRef, useState } from "react";
import { Animated, StyleSheet, Text, View, Easing } from "react-native";
import Svg, {
  Circle,
  Line,
  Defs,
  LinearGradient,
  RadialGradient,
  Stop,
} from "react-native-svg";
import { LoaderUtil } from "../utils/LoaderUtil";

export function AppLoader() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("Loading...");

  const rotateAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(0)).current;

  const dot1 = useRef(new Animated.Value(0)).current;
  const dot2 = useRef(new Animated.Value(0)).current;
  const dot3 = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const unsub = LoaderUtil.subscribe((loading, msg) => {
      setLoading(loading);
      if (msg) setMessage(msg);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!loading) return;

    // Chakra rotation
    Animated.loop(
      Animated.timing(rotateAnim, {
        toValue: 1,
        duration: 900,
        easing: Easing.linear,
        useNativeDriver: true,
      })
    ).start();

    // Aura breathing animation
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 0,
          duration: 1500,
          useNativeDriver: true,
        }),
      ])
    ).start();

    // Dot animations (never stop)
    startDotAnimation(dot1, 0);
    startDotAnimation(dot2, 200);
    startDotAnimation(dot3, 400);

  }, [loading]);

  const spin = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "360deg"],
  });

  const scale = pulseAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0.9, 1.1],
  });

  if (!loading) return null;

  return (
    <View style={styles.overlay}>

      {/* Divine Aura */}
      <Animated.View
        style={{
          position: "absolute",
          transform: [{ scale }],
          opacity: 0.6,
        }}
      >
        <Svg width={220} height={220} viewBox="0 0 100 100">
          <Defs>
            <RadialGradient id="aura" cx="50%" cy="50%" r="50%">
              <Stop offset="0%" stopColor="#FCD34D" stopOpacity="0.7" />
              <Stop offset="50%" stopColor="#F59E0B" stopOpacity="0.3" />
              <Stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
            </RadialGradient>
          </Defs>

          <Circle cx="50" cy="50" r="50" fill="url(#aura)" />
        </Svg>
      </Animated.View>

      {/* Rotating Chakra */}
      <Animated.View style={{ transform: [{ rotate: spin }] }}>
        <Svg width={110} height={110} viewBox="0 0 100 100">
          <Defs>
            <LinearGradient id="gold" x1="0%" y1="0%" x2="100%" y2="100%">
              <Stop offset="0%" stopColor="#F59E0B" />
              <Stop offset="50%" stopColor="#FCD34D" />
              <Stop offset="100%" stopColor="#D97706" />
            </LinearGradient>
          </Defs>

          <Circle cx="50" cy="50" r="48" stroke="url(#gold)" strokeWidth="2" fill="none" />
          <Circle cx="50" cy="50" r="8" fill="url(#gold)" />

          {[...Array(24)].map((_, i) => {
            const angle = (i * 15 * Math.PI) / 180;
            const x = 50 + 45 * Math.cos(angle);
            const y = 50 + 45 * Math.sin(angle);

            return (
              <Line
                key={i}
                x1="50"
                y1="50"
                x2={x}
                y2={y}
                stroke="url(#gold)"
                strokeWidth="1.5"
              />
            );
          })}
        </Svg>
      </Animated.View>

      {/* Text */}
      <Text style={styles.title}>{message}</Text>
      <Text style={styles.subtitle}>Hare Krishna</Text>

      {/* Running dots */}
      <View style={styles.dotRow}>
        <Animated.View style={[styles.dot, { transform: [{ translateY: dot1 }] }]} />
        <Animated.View style={[styles.dot, { transform: [{ translateY: dot2 }] }]} />
        <Animated.View style={[styles.dot, { transform: [{ translateY: dot3 }] }]} />
      </View>

    </View>
  );
}

function startDotAnimation(dot: Animated.Value, delay: number) {
  Animated.loop(
    Animated.sequence([
      Animated.delay(delay),
      Animated.timing(dot, {
        toValue: -10,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(dot, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.delay(300),
    ])
  ).start();
}

const styles = StyleSheet.create({
  overlay: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.75)",
    zIndex: 9999,
  },

  title: {
    marginTop: 18,
    fontSize: 18,
    fontWeight: "700",
    color: "#7C2D12",
  },

  subtitle: {
    fontSize: 13,
    marginTop: 4,
    color: "#EA580C",
  },

  dotRow: {
    flexDirection: "row",
    marginTop: 10,
    gap: 6,
  },

  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: "#EA580C",
  },
});